import React, { useState, useEffect } from 'react';
import { PencilIcon, TrashIcon, PlusIcon, ChevronUpIcon, ChevronDownIcon, DocumentDuplicateIcon } from '@heroicons/react/24/outline';
import { apiRequest } from '../../../api/api';
import FilterPanel from '../../../components/ui/FilterPanel';
import FilterActions from '../../../components/ui/FilterActions';
import ActionToolbar from '../../../components/ui/ActionToolbar';
import IconActionButton from '../../../components/ui/IconActionButton';

const BudgetSummaryCards = ({ onEdit, onDelete, onNew, onCreateFromPlanning, refreshTrigger }) => {
  const [budgetData, setBudgetData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [groupedData, setGroupedData] = useState({});
  const [selectedRow, setSelectedRow] = useState(null);

  // Filtre state'leri
  const [filters, setFilters] = useState({
    maingroup: '',
    budgettype: '',
    year: '',
    month: '',
    search: ''
  });
  const [maingroups, setMaingroups] = useState([]);
  const [budgettypes, setBudgettypes] = useState([]);
  const [years, setYears] = useState([]);
  const [months, setMonths] = useState([]);

  // Sıralama state'leri
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  useEffect(() => {
    // Global yükleme sinyali gönder
    window.dispatchEvent(new CustomEvent('app_loading', { detail: loading }));
  }, [loading]);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        fetchBudgetData(),
        fetchFilterOptions()
      ]);
    } catch (error) {
      console.error('Bütçe özet verileri yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, [refreshTrigger]);

  const fetchFilterOptions = async () => {
    try {
      const [maingroupsRes, typesRes] = await Promise.all([
        apiRequest('/marketing-budget/maingroups/'),
        apiRequest('/marketing-budget/types/')
      ]);

      setMaingroups(maingroupsRes.results || maingroupsRes || []);
      setBudgettypes(typesRes.results || typesRes || []);

      // Yılları oluştur (2020-2029)
      const yearsList = Array.from({ length: 10 }, (_, i) => 2020 + i);
      setYears(yearsList);

      // Ayları oluştur (1-12)
      const monthsList = Array.from({ length: 12 }, (_, i) => i + 1);
      setMonths(monthsList);
    } catch (error) {
      // Silently handle error
    }
  };

  const fetchBudgetData = async () => {
    try {
      // Cache-busting için timestamp ekle
      const timestamp = Date.now();
      const response = await apiRequest(`/marketing-budget/registries/?page_size=1000&_t=${timestamp}`);

      const data = response.results || response || [];

      setBudgetData(data);

      // Verileri budget name bazında grupla
      const grouped = data.reduce((acc, item) => {
        if (!acc) acc = {};

        // Null/undefined kontrolü
        if (!item || !item.maingroup_name) {
          return acc;
        }

        // Web Yazılım için özel durum
        if (item.maingroup_name === 'Web Yazılım') {
          const key = item.budget_name || `${item.maingroup_name || 'Diğer'} - ${item.budgettype_name || 'Genel'}`;
          if (!acc[key]) {
            acc[key] = {
              totalAmount: 0,
              entries: [],
              subgroups: new Set(),
              budgetName: key,
              maingroupName: item.maingroup_name,
              budgettypeName: item.budgettype_name,
              budgetYear: item.budget_year,
              budgetMonth: item.budget_month
            };
          }
          acc[key].entries.push(item);
          acc[key].subgroups.add(item.subgroup_name);
          return acc;
        }

        // Dijital Pazarlama için özel durum
        if (item.maingroup_name === 'Dijital Pazarlama') {
          const key = `${item.maingroup_name} - ${item.budgettype_name} (${item.budget_year})`;
          if (!acc[key]) {
            acc[key] = {
              totalAmount: 0,
              entries: [],
              subgroups: new Set(),
              budgetName: key,
              maingroupName: item.maingroup_name,
              budgettypeName: item.budgettype_name,
              budgetYear: item.budget_year,
              budgetMonth: item.budget_month
            };
          }
          acc[key].entries.push(item);
          acc[key].subgroups.add(item.subgroup_name);
          return acc;
        }

        // Temsil Ağırlama / AKKA Bonus için özel gruplama - tüm ayları tek grupta topla
        if (['Temsil Ağırlama', 'AKKA Bonus'].includes(item.maingroup_name)) {
          // Ay bilgisi olmadan, sadece maingroup + budgettype + year ile grupla
          const year = item.budget_year || 'Yıl Yok';
          const key = `${item.maingroup_name} - ${item.budgettype_name || 'Genel'} (${year})`;
          if (!acc[key]) {
            acc[key] = {
              totalAmount: 0,
              entries: [],
              subgroups: new Set(),
              budgetName: key,
              maingroupName: item.maingroup_name,
              budgettypeName: item.budgettype_name || 'Genel',
              budgetYear: item.budget_year,
              budgetMonth: null
            };
          }
          acc[key].totalAmount += parseFloat(item.budget_amount || 0);
          acc[key].entries.push(item);
          if (item.subgroup_name) {
            acc[key].subgroups.add(item.subgroup_name);
          }
          return acc;
        }

        // Diğer ana gruplar için normal mantık
        const key = item.budget_name || `${item.maingroup_name || 'Diğer'} - ${item.budgettype_name || 'Genel'}`;
        if (!acc[key]) {
          acc[key] = {
            totalAmount: 0,
            entries: [],
            subgroups: new Set(),
            budgetName: key,
            maingroupName: item.maingroup_name,
            budgettypeName: item.budgettype_name,
            budgetYear: item.budget_year,
            budgetMonth: item.budget_month
          };
        }
        acc[key].entries.push(item);
        acc[key].subgroups.add(item.subgroup_name);
        return acc;
      }, {});

      // Her grup için toplamı entries dizisinden yeniden hesapla
      Object.keys(grouped).forEach(key => {
        grouped[key].totalAmount = grouped[key].entries.reduce((sum, entry) => {
          return sum + parseFloat(entry.budget_amount || 0);
        }, 0);
      });

      setGroupedData(grouped);
    } catch (error) {
      setBudgetData([]);
      setGroupedData({});
      throw error;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  // Sıralama fonksiyonu
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Filtreleme fonksiyonu
  const getFilteredData = () => {
    return Object.entries(groupedData).filter(([groupName, groupData]) => {
      // Ana grup filtresi
      if (filters.maingroup && groupData.maingroupName !== filters.maingroup) {
        return false;
      }

      // Bütçe tipi filtresi
      if (filters.budgettype && groupData.budgettypeName !== filters.budgettype) {
        return false;
      }

      // Yıl filtresi
      if (filters.year && groupData.budgetYear !== parseInt(filters.year)) {
        return false;
      }

      // Ay filtresi - Her entry'de budget_month kontrol et
      if (filters.month) {
        const hasMonth = groupData.entries.some(entry => entry.budget_month === parseInt(filters.month));
        if (!hasMonth) {
          return false;
        }
      }

      // Arama filtresi
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        return (
          groupData.budgetName.toLowerCase().includes(searchLower) ||
          groupData.maingroupName.toLowerCase().includes(searchLower)
        );
      }

      return true;
    });
  };

  // Sıralanmış ve filtrelenmiş veri
  const getSortedAndFilteredData = () => {
    const filtered = getFilteredData();

    if (!sortConfig.key) return filtered;

    return [...filtered].sort((a, b) => {
      const [, aData] = a;
      const [, bData] = b;

      let aValue, bValue;

      switch (sortConfig.key) {
        case 'budgetName':
          aValue = aData.budgetName || '';
          bValue = bData.budgetName || '';
          break;
        case 'maingroup':
          aValue = aData.maingroupName || '';
          bValue = bData.maingroupName || '';
          break;
        case 'budgettype':
          aValue = aData.budgettypeName || '';
          bValue = bData.budgettypeName || '';
          break;
        case 'year':
          aValue = aData.budgetYear || 0;
          bValue = bData.budgetYear || 0;
          break;
        case 'total':
          aValue = aData.totalAmount || 0;
          bValue = bData.totalAmount || 0;
          break;
        case 'date':
          aValue = aData.entries[0]?.created_date || '';
          bValue = bData.entries[0]?.created_date || '';
          break;
        default:
          return 0;
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  const filteredData = getSortedAndFilteredData();

  const clearFilters = () => {
    setFilters({
      maingroup: '',
      budgettype: '',
      year: '',
      month: '',
      search: '',
    });
  };

  return (
    <div className="space-y-4">
      {/* Aksiyon Butonları */}
      <ActionToolbar>
        <IconActionButton
          icon={PlusIcon}
          onClick={onNew}
          title="Yeni Kayıt"
          className="text-gray-600 hover:text-blue-600 dark:text-gray-300 dark:hover:text-blue-400"
        />

        <IconActionButton
          icon={PencilIcon}
          onClick={() => selectedRow && onEdit(selectedRow.entries)}
          disabled={!selectedRow}
          title="Düzenle"
          className="text-gray-600 hover:text-blue-600 dark:text-gray-300 dark:hover:text-blue-400 disabled:text-gray-300"
        />

        <IconActionButton
          icon={DocumentDuplicateIcon}
          onClick={() => selectedRow && onCreateFromPlanning(selectedRow.entries)}
          disabled={!selectedRow}
          title="Benzerini Oluştur"
          className="text-gray-600 hover:text-blue-600 dark:text-gray-300 dark:hover:text-blue-400 disabled:text-gray-300"
        />

        <IconActionButton
          icon={TrashIcon}
          onClick={() => selectedRow && onDelete(selectedRow.entries)}
          disabled={!selectedRow}
          title="Sil"
          className="text-gray-600 hover:text-red-600 dark:text-gray-300 dark:hover:text-red-400 disabled:text-gray-300"
        />

        <div className="ml-auto text-sm text-gray-600 dark:text-gray-400">
          {selectedRow ? (
            <span>
              <span className="font-semibold text-blue-600 dark:text-blue-400">{selectedRow.budgetName}</span> seçildi
            </span>
          ) : (
            <span>Toplam <span className="font-semibold">{filteredData.length}</span> kayıt</span>
          )}
        </div>
      </ActionToolbar>

      <FilterPanel title="Filters" isOpen>
        <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          {/* Arama */}
          <input
            type="text"
            placeholder="Ara..."
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-colors duration-200"
          />

          {/* Ana Grup */}
          <select
            value={filters.maingroup}
            onChange={(e) => setFilters({ ...filters, maingroup: e.target.value })}
            className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-colors duration-200"
          >
            <option value="">Tüm Ana Gruplar</option>
            {maingroups.map((mg) => (
              <option key={mg.id} value={mg.maingroup_name}>
                {mg.maingroup_name}
              </option>
            ))}
          </select>

          {/* Bütçe Tipi */}
          <select
            value={filters.budgettype}
            onChange={(e) => setFilters({ ...filters, budgettype: e.target.value })}
            className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-colors duration-200"
          >
            <option value="">Tüm Bütçe Tipleri</option>
            {budgettypes.map((bt) => (
              <option key={bt.id} value={bt.budget_type}>
                {bt.budget_type}
              </option>
            ))}
          </select>

          {/* Yıl */}
          <select
            value={filters.year}
            onChange={(e) => setFilters({ ...filters, year: e.target.value })}
            className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-colors duration-200"
          >
            <option value="">Tüm Yıllar</option>
            {years.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>

          {/* Ay */}
          <select
            value={filters.month}
            onChange={(e) => setFilters({ ...filters, month: e.target.value })}
            className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-colors duration-200"
          >
            <option value="">Tüm Aylar</option>
            {months.map((month) => (
              <option key={month} value={month}>
                {new Date(2024, month - 1).toLocaleString('tr-TR', { month: 'long' })}
              </option>
            ))}
          </select>
        </div>
        <div className="border-t border-gray-100 dark:border-gray-700 pt-3">
          <FilterActions onSearch={fetchBudgetData} onClear={clearFilters} />
        </div>
        </div>
      </FilterPanel>

      {/* Tablo */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        {filteredData.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 dark:text-gray-400">
              {filters.search || filters.maingroup || filters.budgettype || filters.year
                ? 'Filtreye uygun kayıt bulunamadı.'
                : 'Henüz bütçe kaydı bulunmuyor.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th
                    onClick={() => handleSort('budgetName')}
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer select-none"
                  >
                    <div className="flex items-center gap-1">
                      <span>Bütçe Adı</span>
                      {sortConfig.key === 'budgetName' && (
                        sortConfig.direction === 'asc' ?
                          <ChevronUpIcon className="h-4 w-4" /> :
                          <ChevronDownIcon className="h-4 w-4" />
                      )}
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('maingroup')}
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer select-none"
                  >
                    <div className="flex items-center gap-1">
                      <span>Ana Grup</span>
                      {sortConfig.key === 'maingroup' && (
                        sortConfig.direction === 'asc' ?
                          <ChevronUpIcon className="h-4 w-4" /> :
                          <ChevronDownIcon className="h-4 w-4" />
                      )}
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('budgettype')}
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer select-none"
                  >
                    <div className="flex items-center gap-1">
                      <span>Bütçe Tipi</span>
                      {sortConfig.key === 'budgettype' && (
                        sortConfig.direction === 'asc' ?
                          <ChevronUpIcon className="h-4 w-4" /> :
                          <ChevronDownIcon className="h-4 w-4" />
                      )}
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('year')}
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer select-none"
                  >
                    <div className="flex items-center gap-1">
                      <span>Yıl</span>
                      {sortConfig.key === 'year' && (
                        sortConfig.direction === 'asc' ?
                          <ChevronUpIcon className="h-4 w-4" /> :
                          <ChevronDownIcon className="h-4 w-4" />
                      )}
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('total')}
                    className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer select-none"
                  >
                    <div className="flex items-center justify-end gap-1">
                      <span>Toplam</span>
                      {sortConfig.key === 'total' && (
                        sortConfig.direction === 'asc' ?
                          <ChevronUpIcon className="h-4 w-4" /> :
                          <ChevronDownIcon className="h-4 w-4" />
                      )}
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('date')}
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer select-none"
                  >
                    <div className="flex items-center gap-1">
                      <span>Son Güncelleme</span>
                      {sortConfig.key === 'date' && (
                        sortConfig.direction === 'asc' ?
                          <ChevronUpIcon className="h-4 w-4" /> :
                          <ChevronDownIcon className="h-4 w-4" />
                      )}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {filteredData.map(([groupName, groupData]) => (
                  <tr
                    key={groupName}
                    onClick={() => setSelectedRow(selectedRow?.budgetName === groupData.budgetName ? null : groupData)}
                    className={`cursor-pointer transition-colors duration-200 ${selectedRow?.budgetName === groupData.budgetName
                      ? 'bg-blue-50 dark:bg-blue-900/20 border-l-4 border-l-blue-500'
                      : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                  >
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                      {groupData.budgetName}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                      {groupData.maingroupName}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                      {groupData.budgettypeName}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                      {groupData.budgetYear || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm font-semibold text-right text-green-600 dark:text-green-400">
                      {formatCurrency(groupData.totalAmount)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                      {formatDate(groupData.entries[0]?.created_date)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default BudgetSummaryCards;
